import { useState, useEffect } from "react";
import { Package, Loader2, Mail, Clock, CheckCircle, Truck, Box } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Order {
  id: string;
  product_id: string;
  variant_id: string;
  quantity: number;
  total_price: number;
  status: string;
  created_at: string;
  product?: {
    name: string;
    cover_image: string;
  };
}

const statusLabels: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  "pending": { label: "Da prendere in ordinazione", icon: <Clock className="w-4 h-4" />, color: "text-amber-500 bg-amber-500/10" },
  "confirmed": { label: "Preso in ordinazione", icon: <CheckCircle className="w-4 h-4" />, color: "text-blue-500 bg-blue-500/10" },
  "preparing": { label: "Preparazione", icon: <Truck className="w-4 h-4" />, color: "text-purple-500 bg-purple-500/10" },
  "packaging": { label: "Imballaggio", icon: <Box className="w-4 h-4" />, color: "text-yellow-500 bg-yellow-500/10" },
  "ready_pickup": { label: "Pronto da ritirare", icon: <CheckCircle className="w-4 h-4" />, color: "text-green-500 bg-green-500/10" },
  "ready_delivery": { label: "Pronto per la consegna", icon: <Truck className="w-4 h-4" />, color: "text-green-500 bg-green-500/10" },
  "picked_up": { label: "Ritirato", icon: <Box className="w-4 h-4" />, color: "text-violet-500 bg-violet-500/10" },
  "delivered": { label: "Consegnato", icon: <CheckCircle className="w-4 h-4" />, color: "text-violet-500 bg-violet-500/10" },
};

export const UserOrders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
    
    // Set up realtime subscription
    const channel = supabase
      .channel('user-orders')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
        },
        (payload) => {
          setOrders(prev => prev.map(order => 
            order.id === payload.new.id 
              ? { ...order, status: payload.new.status }
              : order
          ));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchOrders = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          product:products(name, cover_image)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOrders(data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Errore nel caricamento degli ordini');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Package className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground font-medium">Al momento non ci sono ordini effettuati</p>
            <p className="text-sm text-muted-foreground/70 mt-1">
              I tuoi ordini appariranno qui
            </p>
          </div>
        ) : (
          orders.map((order) => {
            const statusInfo = statusLabels[order.status] || statusLabels.pending;
            
            return (
              <div
                key={order.id}
                className="p-4 rounded-xl bg-card/80 border border-border/50 space-y-3"
              >
                <div className="flex items-start gap-3">
                  {order.product?.cover_image && (
                    <img 
                      src={order.product.cover_image} 
                      alt={order.product.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground truncate">
                      {order.product?.name || 'Prodotto'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Quantità: {order.quantity} • €{order.total_price.toFixed(2)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDate(order.created_at)}
                    </p>
                  </div>
                </div>
                
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${statusInfo.color}`}>
                  {statusInfo.icon}
                  <span>{statusInfo.label}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
      
      {/* Support Button - Always visible */}
      <div className="p-4 border-t border-border/50">
        <a
          href="mailto:assistenza.app26@gmail.com"
          className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-primary text-primary-foreground font-medium rounded-xl transition-all duration-300 hover:bg-primary/90 hover:scale-[1.02] active:scale-[0.98]"
        >
          <Mail className="w-5 h-5" />
          <span>Contattaci</span>
        </a>
      </div>
    </div>
  );
};

export default UserOrders;
